import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { FormDateFieldDS, FormSelectFieldDS } from 'components/reusable/form/FormFields'
import { Button, ButtonGroup, TextLink } from 'slab'
import { getDateStringNoTimezone } from 'utils'
import { getMemberOnboardingEnrollmentEventsQleMetadata } from 'utils/web_api'
import {
  type MemberOnboardingQleMetadata,
  type PatchMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentSubtype,
} from 'utils/web_api.schemas'

import { type GrapeAPIErrorType } from './hooks/GrapeAPIError'
import { QleCoverageStartBanner } from './QleCoverageStartBanner'
import { type QleErrorCode, QleStartErrorBanner } from './QleStartErrorBanner'

type QleType = PatchMemberOnboardingEnrollmentEventsEnrollmentEventIdEnrollmentSubtype
interface QleFormData {
  qleType: QleType
  qleDate: string
}

const QLE_OPTIONS: { label: string, value: QleType }[] = [
  { label: 'Married', value: 'marriage' },
  { label: 'Divorced', value: 'divorce_or_legal_separation' },
  { label: 'Added a child', value: 'birth_or_adoption' },
  { label: 'Moved', value: 'relocation' },
  { label: 'Lost coverage', value: 'loss_of_coverage' },
  { label: 'Death of dependent', value: 'death_of_dependent' },
  { label: 'Left incarceration', value: 'left_incarceration' },
  { label: 'Immigrated', value: 'immigrated' },
  { label: 'Received a court order', value: 'court_ordered' },
  { label: 'Newly Medicare-eligible', value: 'medicare' },
  { label: 'Complex or unique scenario', value: 'other' },
]

// Used to prevent refiring GET /qle_metadata when mid-typing the date, see qleMetadataParams
const isValidDate = (dateString: string | undefined): boolean => {
  if (!dateString) return false
  const year = new Date(dateString).getFullYear()
  return year >= 2000 && year <= 3000
}

export type QleDetailsRenderButtonLayoutProps = {
  submit: () => void;
  disabled: boolean;
}

// This is basically all of the stuff on the page for the QleStartPage and QleDetailsPage.
export const QleDetails = ({
  onSubmit,
  initialQleType,
  initialQleDate,
  renderButtonLayout,
}: {
  onSubmit: (params: { qleType: QleType, qleDate: Date }) => Promise<void>;
  initialQleType?: QleType;
  initialQleDate?: string;
  renderButtonLayout?: (props: QleDetailsRenderButtonLayoutProps) => React.ReactNode;
}) => {
  const [submitError, setSubmitError] = useState<GrapeAPIErrorType | null>(null)
  const [qleMetadata, setQleMetadata] = useState<MemberOnboardingQleMetadata | null>(null)
  const [timeWindowErrorCode, setTimeWindowErrorCode] = useState<QleErrorCode | null>(null)
  const { register, handleSubmit, formState: { errors }, watch } = useForm<QleFormData>({
    defaultValues: {
      qleType: initialQleType,
      qleDate: initialQleDate,
    },
  })

  const qleType = watch('qleType')
  const qleDate = watch('qleDate')

  // Calculate min/max dates for the date picker (4 months before/after today)
  // TODO(@ashley): can update this to be dependent on the qleType, see get_qle_window.rb
  const { minDate, maxDate } = useMemo(() => {
    const today = new Date()
    const min = new Date(today.getFullYear(), today.getMonth() - 4, today.getDate())
    const max = new Date(today.getFullYear(), today.getMonth() + 4, today.getDate())
    return { minDate: getDateStringNoTimezone(min), maxDate: getDateStringNoTimezone(max) }
  }, [])

  // Memoize API parameters to only refetch when qleType or qleDate changes
  const qleMetadataParams = useMemo(() => {
    if (!qleType || !isValidDate(qleDate)) return null

    return {
      qle_type: qleType,
      provided_qle_date: qleDate,
    }
  }, [qleType, qleDate])

  // Clear submit error when qleType or qleDate changes (using memoized params to avoid unnecessary updates)
  useEffect(() => {
    if (submitError) {
      setSubmitError(null)
    }
  }, [qleMetadataParams])

  // Fetch QLE metadata when params change
  useEffect(() => {
    if (!qleMetadataParams) {
      setQleMetadata(null)
      setTimeWindowErrorCode(null)
      return
    }

    const fetchQleMetadata = async () => {
      try {
        const metadata = await getMemberOnboardingEnrollmentEventsQleMetadata(qleMetadataParams)
        setQleMetadata(metadata)

        // Validate that current time is within the enrollment window
        const now = new Date()
        const earliestTimeToStart = new Date(metadata.earliest_time_to_start)
        const latestTimeToSubmit = new Date(metadata.latest_time_to_submit)

        if (now < earliestTimeToStart) {
          setTimeWindowErrorCode('qle_date_too_early')
        } else if (now > latestTimeToSubmit) {
          setTimeWindowErrorCode('qle_date_expired')
        } else {
          setTimeWindowErrorCode(null)
        }
      } catch {
        // Silently fail - no need to display error
        setTimeWindowErrorCode(null)
      }
    }

    fetchQleMetadata().catch(() => {
      // Catch any unexpected errors from the async function
    })
  }, [qleMetadataParams])

  const handleFormSubmit = async (data: QleFormData) => {
    setSubmitError(null)
    try {
      await onSubmit({ qleType: data.qleType, qleDate: new Date(data.qleDate) })
    } catch (err) {
      setSubmitError(err as GrapeAPIErrorType)
    }
  }

  return (
    <form className='w-full max-w-[44rem]' onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Heading and subheading */}
      <div className='heading-lg mb-6'>Qualifying life event details</div>
      <div className='body text-gray-500 mb-6'>
        {'You can choose different plans, or who\'s on them (and what allowance you get as a result) if you\'ve recently experienced a '}
        <TextLink
          href='https://support.thatch.com/qualifying-life-event'
          target='_blank'
        >
          qualifying life event
        </TextLink>
      </div>
      {/* QLE inputs */}
      <div className='heading-sm mb-4'>What's changed?</div>
      {/* QLE type */}
      <div className='flex flex-col w-fit'>
        <div className='mb-6 flex'>
          <FormSelectFieldDS<QleFormData>
            fieldId='qleType'
            fieldLabel='Life event'
            fieldOptions={QLE_OPTIONS}
            placeholder='Select'
            register={register}
            error={errors.qleType}
            description={
              <>
                Don't see yours listed? <TextLink href='mailto:support@thatch.com' target='_blank' color='purple'>Reach out to support</TextLink>
              </>
            }
            validationRule={{ required: 'Please select a qualifying life event' }}
          />
        </div>
        {/* QLE date */}
        <div className='w-full'>
          <FormDateFieldDS<QleFormData>
            fieldId='qleDate'
            fieldLabel='Event date'
            register={register}
            error={errors.qleDate}
            validationRule={{
              required: 'Please enter the date of the qualifying life event',
              validate: (value: string) => isValidDate(value) || 'Please enter a valid date',
            }}
            min={minDate}
            max={maxDate}
          />
        </div>
      </div>
      {/* QLE coverage start banner */}
      {isValidDate(qleDate) && qleMetadata && !timeWindowErrorCode && (
        <div className='mt-6'>
          <QleCoverageStartBanner
            qleType={qleMetadata.qle_type as QleType}
            standardCoverageStartDate={qleMetadata.standard_coverage_start_date}
            backdatedCoverageStartDate={qleMetadata.backdated_coverage_start_date ?? ''}
            submitByDate={qleMetadata.submit_by_for_earliest_coverage_start_date}
          />
        </div>
      )}
      {/* QLE error banner */}
      {isValidDate(qleDate) && (timeWindowErrorCode || submitError) && (
        <div className='mt-6'>
          <QleStartErrorBanner
            qleErrorCode={timeWindowErrorCode}
            metadata={qleMetadata}
            qleError={submitError}
          />
        </div>
      )}
      {/* Back/Next buttons */}
      <div className='mt-10'>
        {renderButtonLayout
          ? (
              renderButtonLayout({
                submit: handleSubmit(handleFormSubmit),
                disabled: !!timeWindowErrorCode || !!submitError,
              })
            )
          : (
            <ButtonGroup>
              <Button variant='primary' type='submit' disabled={!!timeWindowErrorCode || !!submitError}>
                Next
              </Button>
            </ButtonGroup>
            )}
      </div>
    </form>
  )
}
